<template>
  <v-dialog :model-value="updateType !== null" width="600" @close="updateType = null" persistent>
    <v-card v-if="updateType">
      <v-card-title class="d-flex flex-row ma-2">
        <h4 class="text-capitalize" data-testid="yard-spot-assignment-dialog-title">
          <v-icon size="small" class="mr-2">mdi-alert-circle-outline</v-icon>
          <span v-if="updateType === 'unassign'">
            {{ t('yard.dialogs.yardViewSpotAssignmentUpdateDialog.headers.confirmUnassignment') }}?
          </span>
          <span v-else-if="updateType === 'depart'">
            {{
              t('yard.dialogs.yardViewSpotAssignmentUpdateDialog.headers.confirmAssetDeparture')
            }}?
          </span>
        </h4>
        <v-spacer></v-spacer>
        <v-btn icon="mdi-close" variant="text" density="comfortable" @click="closeDialog"></v-btn>
      </v-card-title>
      <v-card-text>
        <template v-if="updateType === 'depart'">
          <yard-view-depart-details :asset="assetVisit" />
        </template>

        <template v-else>
          <p>
            {{
              t(
                'yard.dialogs.yardViewSpotAssignmentUpdateDialog.messages.thisAssetWillBeRemovedFromThisSpotAndMovedBackToTheWaitingList'
              )
            }}
          </p>

          <p class="my-4" v-if="spotAssignment.assetVisit && spotAssignment.assetContainer">
            {{
              t(
                'yard.dialogs.yardViewSpotAssignmentUpdateDialog.messages.bothTheTruckAndAssetWillBeRemovedFromThisSpot'
              )
            }}
          </p>
        </template>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          variant="outlined"
          @click="closeDialog"
          data-testid="yard-spot-assignment-dialog-cancel">
          {{ t('global.buttons.nevermind') }}
        </v-btn>
        <v-btn class="bg-primary" @click="submit" data-testid="yard-spot-assignment-dialog-submit">
          {{ t('global.buttons.confirm') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent, computed } from 'vue';
import useYard from '@/modules/yard/composables/useYard';
import { useEventHub } from '@/composables';
import { DepartTypeEnum } from '@nova/core';
import YardViewDepartDetails from '@/modules/yard/components/AssetVisit/YardViewDepartDetails.vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewSpotAssignmentUpdateDialog',
  components: {
    YardViewDepartDetails
  },
  props: {
    updateType: {
      type: [String, null],
      default: null,
      validator: value => ['unassign', 'depart', null].includes(value)
    },
    spotAssignment: {
      type: Object,
      required: true
    },
    // This comes from the activeTab on the assigned spot detail
    assetType: {
      type: String,
      required: true,
      validator: value => Object.values(DepartTypeEnum).includes(value)
    }
  },
  setup(props) {
    const { t } = useI18n();
    const yardStore = useYard();
    const eventHub = useEventHub();

    const assetVisit = computed(() => {
      if (
        (props.assetType === DepartTypeEnum.AssetContainer &&
          props.spotAssignment.assetContainer) ||
        !props.spotAssignment.assetVisit
      ) {
        return props.spotAssignment.assetContainer;
      }
      return props.spotAssignment.assetVisit;
    });

    const submit = () => {
      const spotAssignment = { ...props.spotAssignment };

      let departType = props.assetType;

      // When one of the asset visit or asset container is not present, we need to set the depart type to All
      // to ensure that the single item on the spot is departed
      if (!spotAssignment.assetVisit?.id || !spotAssignment.assetContainer?.id) {
        departType = DepartTypeEnum.All;
      }

      if (props.updateType === 'depart') {
        yardStore.departSpotAssignment(spotAssignment.id, departType).then(() => {
          if (spotAssignment.assetVisit) {
            yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.DEPART, {
              assetVisit: spotAssignment.assetVisit,
              metadata: {
                departed_with_asset_container: Boolean(
                  spotAssignment.assetVisit.assetContainers?.length > 0
                ),
                appointment_id: spotAssignment.assetVisit.appointmentId
              }
            });
          }
        });
      }
      if (props.updateType === 'unassign') {
        yardStore.deleteSpotAssignment(spotAssignment.id).then(() => {
          yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.UNASSIGNED, {
            assetVisit: spotAssignment.assetVisit ?? spotAssignment.assetContainer,
            metadata: {
              is_asset_container: yardStore.isAssetContainer(
                spotAssignment.assetVisit ?? spotAssignment.assetContainer
              )
            }
          });
        });
      }
      closeDialog();
    };

    const closeDialog = () => {
      eventHub.$emit('close-spot-assignment-update-dialog');
    };

    return {
      t,
      submit,
      closeDialog,
      assetVisit
    };
  }
});
</script>
